
import {
    CONFERENCE_JOINED,
    CONFERENCE_WILL_LEAVE,
    IS_CUSTOM_PARTICIPANT_EVERYONE,
    IS_CUSTOM_PARTICIPANT_VISIBLE
} from '../base/conference';
import { PICTURE_IN_PICTURE_MODE } from '../base/config/actionTypes.js';
import { MEDIA_TYPE } from '../base/media';
import { FAKE_PARTICPANT_JOINED, FAKE_PARTICPANT_LEFT } from '../base/mtApi/actionTypes.js';
import {
    PARTICIPANT_JOINED,
    PARTICIPANT_LEFT,
    PARTICIPANT_UPDATED,
    PIN_PARTICIPANT,
    SET_MODERATOR_PIN,
    getLocalParticipant,
    getParticipantById
} from '../base/participants';
import { MiddlewareRegistry } from '../base/redux';
import { TRACK_ADDED, TRACK_REMOVED, TRACK_STOPPED } from '../base/tracks';
import { handlePinParticipantFilmstrip } from '../conference/filmStripViewManager';
import { SET_DOCUMENT_EDITING_STATUS } from '../etherpad/actionTypes.js';
import { SET_FILMSTRIP_VISIBLE } from '../filmstrip';
import { SET_GENERICIFRAME_VISIBILITY_STATUS } from '../genericiframe/actionTypes.js';
import { selectParticipantInLargeVideo } from '../large-video/actions';
import {
    NOTES_PANE_CLOSE,
    NOTES_PANE_OPEN
} from '../notes/actionTypes';
import {
    MODERATION_PANE_CLOSE,
    MODERATION_PANE_OPEN,
    PARTICIPANTS_PANE_CLOSE,
    PARTICIPANTS_PANE_OPEN
} from '../participants-pane/actionTypes.js';
import { SET_WHITEBOARD_INSTANCE } from '../toolbox/actionTypes.js';
import { iAmVisitor } from '../visitors/functions';
import { LOCAL_VIDEO_ZOOM_CHANGED, REMOTE_VIDEO_ZOOM_CHANGED } from '../zoom-sync';

import VideoLayout from './VideoLayout';
import './middleware.any';

declare var APP: Object;

/**
 * Middleware which intercepts actions and updates the legacy component
 * {@code VideoLayout} as needed. The purpose of this middleware is to redux-ify
 * {@code VideoLayout} without having to simultaneously react-ifying it.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */

MiddlewareRegistry.register(store => next => action => {
    // Purposefully perform additional actions after state update to mimic
    // being connected to the store for updates.
    const result = next(action);

    switch (action.type) {
    case CONFERENCE_WILL_LEAVE:
        VideoLayout.reset();
        break;

    case CONFERENCE_JOINED: {
        // The in-page visitor redirect (demote to a visitor node, or promote back to
        // the main room) tears down the legacy VideoLayout via CONFERENCE_WILL_LEAVE ->
        // VideoLayout.reset(), but unlike the breakout-room path (conference.joinRoom ->
        // APP.UI.start()) nothing re-initializes it before the new conference is joined.
        // Without a LargeVideoManager the stage stays hidden (inline display: none) and
        // empty in stage/vertical/horizontal layouts. Re-create it here. Visitors get
        // only the large video re-init on purpose - they must not have a local thumbnail.
        const joinedAsVisitor = iAmVisitor(store.getState());

        if (!VideoLayout.getLargeVideo()) {
            if (joinedAsVisitor) {
                VideoLayout.initLargeVideo();
                VideoLayout.resizeVideoArea();
            } else if (typeof APP !== 'undefined') {
                APP.UI.start();
            }
        }

        if (joinedAsVisitor) {
            // Stale large-video.participantId is often still the local id from before
            // the vnode redirect. Elect a remote (or clear) instead of painting an
            // empty blue local stage next to the host thumbnail.
            store.dispatch(selectParticipantInLargeVideo());
            VideoLayout.refreshLayout();

            // React commits #largeVideo after this middleware returns. Attach on
            // the next frame so stage/vertical actually get the remote track.
            const attachRemoteOnStage = () => {
                const participantId = store.getState()['features/large-video'].participantId;
                const local = getLocalParticipant(store.getState());

                if (participantId && participantId !== local?.id) {
                    VideoLayout.updateLargeVideo(participantId, true);
                }
            };

            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(attachRemoteOnStage);
            } else {
                setTimeout(attachRemoteOnStage, 0);
            }
        } else {
            const { participantId } = store.getState()['features/large-video'];

            participantId && VideoLayout.updateLargeVideo(participantId, true);
        }
        break;
    }

    case PARTICIPANT_JOINED:
        if (!action.participant.local) {
            VideoLayout.addRemoteParticipantContainer(
          getParticipantById(store.getState(), action.participant.id)
            );
        }
        break;

    case TRACK_STOPPED: {
        const { mhTrack } = action.track;

        if (mhTrack.isLocal()) {
            const participant = getLocalParticipant(store.getState());

            VideoLayout._updateLargeVideoIfDisplayed(participant?.id);
        }
        break;
    }

    case PARTICIPANT_LEFT:
        VideoLayout.removeParticipantContainer(action.participant.id);
        break;

    case FAKE_PARTICPANT_JOINED: {
        const { id, name, fakeParticipant, conference } = action;

        // Create containers for all fake participants including LocalScreenShare
        // VideoLayout.addScreenShareParticipantContainer will handle the routing

        VideoLayout.addScreenShareParticipantContainer(action, {
            fakeParticipant,
            conference,
            id,
            name
        });
        break;
    }

    case PARTICIPANT_UPDATED: {
        // Look for actions that triggered a change to connectionStatus. This is
        // done instead of changing the connection status change action to be
        // explicit in order to minimize changes to other code.
        if (typeof action.participant.connectionStatus !== 'undefined') {
            VideoLayout.onParticipantConnectionStatusChanged(
          action.participant.id,
          action.participant.connectionStatus
            );
        }

        break;
    }

    case FAKE_PARTICPANT_LEFT:
        VideoLayout.removeParticipantContainer(action.id, action.fakeParticipant);
        break;

    case PIN_PARTICIPANT: {
        // The middleware runs AFTER the reducer, so we check the final state
        const state = store.getState();
        const actionParticipantId = action.participant?.id;

        // Check if the participant is pinned AFTER the action has been processed
        // Handle local participant specially as it might have different ID handling
        let participant = null;

        if (actionParticipantId) {
            const localParticipant = getLocalParticipant(state);

            if (localParticipant && localParticipant.id === actionParticipantId) {
                participant = localParticipant;
            } else {
                participant = getParticipantById(state, actionParticipantId);
            }
        }

        const isNowPinned = participant?.pinned || false;

        // Pass the correct value to onPinChange: participant ID if pinned, null if unpinned
        VideoLayout.onPinChange(isNowPinned ? actionParticipantId : null);
        break;
    }

    case SET_MODERATOR_PIN: {
        // For moderator pin, check the action to determine if pinning or unpinning
        const actionParticipantId = action.participant?.id;
        const pinAction = action.participant?.action || 'pin';

        if (pinAction === 'pin' || pinAction === 'unpin') {
            const state = store.getState();

            handlePinParticipantFilmstrip(store.dispatch, state, 'SET_MODERATOR_PIN_middleware', pinAction === 'pin' ? action.participant : { id: null });
        }

        // Pass the correct value to onPinChange: participant ID if pinning, null if unpinning
        VideoLayout.onPinChange(pinAction === 'pin' ? actionParticipantId : null);
        break;
    }

    case PICTURE_IN_PICTURE_MODE: {
        VideoLayout.updateAllVideos();
        break;
    }

    case PARTICIPANTS_PANE_CLOSE:
    case PARTICIPANTS_PANE_OPEN:
    case MODERATION_PANE_CLOSE:
    case MODERATION_PANE_OPEN:
    case NOTES_PANE_CLOSE:
    case NOTES_PANE_OPEN:
    case SET_FILMSTRIP_VISIBLE:
    case IS_CUSTOM_PARTICIPANT_EVERYONE:
    case IS_CUSTOM_PARTICIPANT_VISIBLE:
    case SET_GENERICIFRAME_VISIBILITY_STATUS:
    case SET_DOCUMENT_EDITING_STATUS:
    case SET_WHITEBOARD_INSTANCE:
        VideoLayout.resizeVideoArea();
        break;

    case TRACK_ADDED:
        // Screenshare video elements are managed by the React filmstrip.
        if (action.track.mediaType !== MEDIA_TYPE.SCREENSHARE
                && !action.track.local && action.track.mediaType !== MEDIA_TYPE.AUDIO) {
            VideoLayout.onRemoteStreamAdded(action.track.mhTrack);
        }

        break;
    case TRACK_REMOVED:
        if (action.track.mediaType === MEDIA_TYPE.SCREENSHARE) {
            // Handle screenshare track removal for both local and remote

            VideoLayout.onRemoteStreamRemoved(action.track.mhTrack);
        } else if (!action.track.local && action.track.mediaType !== MEDIA_TYPE.AUDIO) {
            // Handle regular video tracks for remote participants
            VideoLayout.onRemoteStreamRemoved(action.track.mhTrack);
        }
        break;

    case REMOTE_VIDEO_ZOOM_CHANGED: {
        const smallVideo = VideoLayout.getSmallVideo(action.participantId);

        if (smallVideo && smallVideo.updateZoomTransform) {
            smallVideo.updateZoomTransform();
        } else if (VideoLayout.updateAllZoomTransforms) {
            VideoLayout.updateAllZoomTransforms();
        }
        break;
    }

    case LOCAL_VIDEO_ZOOM_CHANGED: {
        const localId = getLocalParticipant(store.getState())?.id;
        const localThumb = localId ? VideoLayout.getSmallVideo(localId) : null;

        if (localThumb && localThumb.updateZoomTransform) {
            localThumb.updateZoomTransform();
        } else if (VideoLayout.updateAllZoomTransforms) {
            VideoLayout.updateAllZoomTransforms();
        }
        break;
    }
    }

    return result;
});
